import { ListResponseModel, toQueryString } from ".";
import { CreditInfo } from "../model/credit-info/credit-info";
import { ProviderCustomerOrder, SolarOrder } from "../model/customer/customer";
import { Order } from "../model/loan-request";
import { LoanDetails, MandateStatus, NextOrderPayment, CostOverviewDetails, OrderOfferModel, OrderQuery, CountResponse, OrderType, OrderStatus } from "../model/orders/orders";
import { PaymentMethod } from "../model/payment-method";
import { CustomerPaymentHistory } from "../model/payments/payments";
import { SolarProviderCustomerQuery } from "../model/provider/provider-dashboard-data";
import { TransactionType } from "../model/transaction";
import { apiSlice } from "../stores/base/api-slice";

const orderEndpoints = apiSlice.injectEndpoints({
	endpoints: builder => ({
		getZMBCreditInfoDetail: builder.query<CreditInfo, {CustomerId: string}>({
			query: params => ({
				url: `/approval/credit-info/ZMB/admin`,
				method: "GET",
				params
			}),
		}),
		getNGNCreditInfoDetail: builder.query<CreditInfo, {CustomerId: string}>({
			query: params => ({
				url: `/approval/credit-info/NGN/admin`,
				method: "GET",
				params
			}),
		}),
		getLoanRequest: builder.query<ListResponseModel<Array<Order>>, {
			Page?: number;
			pageSize?: number;
			Type?: string;
			search?: string;
			country: string;
		}>({
			query: (params) => ({
				url: `/loan-requests/get-pages`,
				method: "GET",
				params,
			}),
			providesTags: ["LoanRequest"],
		}),
		getOrders: builder.query<ListResponseModel<Array<OrderType>>, {
			page?: number;
			pageSize?: number;
			status?: OrderStatus;
			paymentMethod?: string;
			search?: string;
		}>({
			query: (params) => ({
				url: `/orders`,
				method: "GET",
				params,
			}),
			providesTags: ["Orders", "CustomerOrders"],
		}),
		getBnplOfferDetails: builder.query<OrderOfferModel, string>({
			query: id => ({
				url: `/orders/bnpl/${id}`,
				method: "GET",
			}),
		}),
		declineBnplOffer: builder.mutation<any, string>({
			query: id => ({
				url: `/orders/bnpl/decline/${id}`,
				method: "POST",
			}),
		}),
		startInstallation: builder.mutation<any, string>({
			query: orderId => ({
				url: `/order/start-installation`,
				method: "POST",
				body: { orderId }
			}),
		}),
		verifyInstallation: builder.mutation<any, {orderId: string, meterNumber?: string}>({
			query: data => ({
				url: `/order/verify-installation`,
				method: "POST",
				body: data,
			}),
		}),
		customerVerifyInstallationNoIssue: builder.mutation<any, { orderId: string; rating: number }>({
			query: data => ({
				url: `/order/customer-verify-installation-no-issue`,
				method: "POST",
				body: data,
			}),
			invalidatesTags: ["Orders", "CustomerOrders"],
		}),
		customerVerifyInstallationWithIssue: builder.mutation<any, { orderId: string; type: string; comment: string }>({
			query: data => ({
				url: `/order/customer-verify-installation-with-issue`,
				method: "POST",
				body: data,
			}),
			invalidatesTags: ["Orders", "CustomerOrders"],
		}),

		providerGetOrders: builder.query<ListResponseModel<Array<SolarOrder>>, SolarProviderCustomerQuery>({
			providesTags: ["Orders"],
			query: queryString => ({
				url: `/order/company` + toQueryString(queryString),
				method: "GET",
			}),
			// transformResponse(response: ListResponseModel<Array<SolarOrder>>) {
			// 	if (response.items) {
			// 		response.items = response.items.map(x => {
			// 			if (x.paymentDate) {
			// 				x.paymentDate = new Date(x.paymentDate);
			// 			}
			// 			return x;
			// 		});
			// 	}
			// 	return response;
			// },
		}),
		updateOrderStatus: builder.mutation<string, any>({
			invalidatesTags: ["Orders"],
			query: req => ({
				url: `/orders/set-new-order-status`,
				method: "POST",
				body: req,
			}),
		}),
		getOrderNextPayment: builder.query<NextOrderPayment, string>({
			query: orderId => ({
				url: `/orders/next-payment/${orderId}`,
				method: "GET",
			}),
		}),
		checkMandateConnected: builder.query<MandateStatus, string>({
			query: orderId => ({
				url: `/orders/mandates/connections?orderId=${orderId}`,
				method: "GET",
			}),
		}),
		getOrderOverview: builder.query<SolarOrder, string>({
			query: orderId => ({
				url:  `http://xepp-staging.southafricanorth.cloudapp.azure.com:90/order/${orderId}`,
				method: "GET",
			}),
		}),
		getOrderDetails: builder.query<LoanDetails, string>({
			query: orderId => ({
				url: `/orders/${orderId}/details`,
				method: "GET",
			}),
		}),
		getOrderConfirmationDetails: builder.query<
			CostOverviewDetails,
			{
				solarProductId: string;
				roofTypeId: number;
				numberOfFloors: number;
				paymentMethod: PaymentMethod;
				durationOfPaymentsInMonths: number;
			}
		>({
			query: request => ({
				url: `/orders/order-confirmation-details`,
				params: request,
				method: "GET",
			}),
		}),
		getOrderRepaymentDetails: builder.query<ListResponseModel<Array<CustomerPaymentHistory>>, OrderQuery>({
			query: query => ({
				url: `/orders/${query?.orderId}/repayments` + toQueryString({ Page: query?.Page, PageSize: query?.PageSize }),
				method: "GET",
			}),
		}),
		createAnOrder: builder.mutation<
			{
				orderId: string;
			},
			{
				productId: string;
				durationOfPaymentsInMonths?: number;
				roofType?: number;
				numberOfFloors?: number;
				paymentType?: TransactionType;
				deliveryAddressId?: string;
				paystackCardId?: string;
			}
		>({
			query: request => ({
				url: `/orders`,
				method: "POST",
				body: request,
			}),
		}),
		createAnGuestOrder: builder.mutation<
			{
				orderId: string;
				authorizationUrl: string;
				accessCode: string;
				reference: string;
			},
			{
				productId: string;
				firstName: string;
				lastName: string;
				phoneNumber?: string;
				additionalPhoneNumber?: string;
				email: string;
				additionalInformation?: string;
				roofType?: number;
				numberOfFloors?: number;
				installationAddress: string;
				redirectUrl: string;
				failedRedirectUrl: string;
			}
		>({
			query: request => ({
				url: `/orders/guest-order`,
				method: "POST",
				body: request,
			}),
		}),
		getPaymentConfirmationDetails: builder.mutation<
			CostOverviewDetails,
			{
				orderId: string;
			}
		>({
			query: req => ({
				url: `/orders/payment-confirmation-details?orderId=${req.orderId}`,
				method: "POST",
			}),
		}),
		getGuestOrderInfo: builder.query<
			{
				productName: string;
				customerFullName: string;
				customerEmail: string;
				customerPhoneNumber: string;
				orderNumber: string;
				dateOfPurchase: string;
				amountPaid: number;
				orderStatus: string;
			},
			string
		>({
			query: id => ({
				url: `/orders/guest/${id}`,
				method: "GET",
			}),
		}),
		getOrderCount: builder.query<CountResponse, void>({
			query: () => ({
				url: `/orders/count`,
				method: "GET",
			}),
			providesTags: ["Orders"],
		}),
		acceptLoanRequest: builder.mutation<
			{ actionCodeSent: boolean },
			{
				orderId: string;
				actionCode: string | null;
			}
		>({
			query: (data) => ({
				url: `/orders/accept`,
				method: "POST",
				body: data,
			}),
			invalidatesTags: ["Orders", "CustomerOrders"],
		}),
		declineLoanRequest: builder.mutation<
			{ actionCodeSent: boolean },
			{
				orderId: string;
				actionCode: string | null;
				description?: string;
			}
		>({
			query: (data) => ({
				url: `/orders/decline`,
				method: "POST",
				body: data,
			}),
			invalidatesTags: ["Orders", "CustomerOrders"],
		}),
		createPayAsYouGoOrder: builder.mutation<
			{ orderId: string },
			{ productId: string; recommendationId: string; deliveryAddressId: string }
		>({
			query: request => ({
				url: `/order/pay-as-you-go`,
				method: "POST",
				body: request,
			}),
		}),
	}),
});

export const {
	useGetOrdersQuery,
	useGetLoanRequestQuery,
	useLazyGetBnplOfferDetailsQuery,
	useDeclineBnplOfferMutation,
	useLazyProviderGetOrdersQuery,
	useLazyGetOrderConfirmationDetailsQuery,
	useUpdateOrderStatusMutation,
	useLazyGetOrderNextPaymentQuery,
	useLazyCheckMandateConnectedQuery,
	useLazyGetOrderDetailsQuery,
	useLazyGetOrderRepaymentDetailsQuery,
	useCreateAnOrderMutation,
	useGetPaymentConfirmationDetailsMutation,
	useCreateAnGuestOrderMutation,
	useLazyGetGuestOrderInfoQuery,
	useGetOrderCountQuery,
	useAcceptLoanRequestMutation,
	useDeclineLoanRequestMutation,
	useGetOrderOverviewQuery,
	useGetZMBCreditInfoDetailQuery,
    useGetNGNCreditInfoDetailQuery,
	useStartInstallationMutation,
	useVerifyInstallationMutation,
	useCreatePayAsYouGoOrderMutation,
	useCustomerVerifyInstallationNoIssueMutation,
	useCustomerVerifyInstallationWithIssueMutation,
} = orderEndpoints;
